import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Search, Shield, ShieldAlert, FileCheck, Globe, Package,
  Loader2, CheckCircle, XCircle, AlertTriangle, Clock
} from 'lucide-react';
import api from '../lib/api';
import { useTranslation } from '../i18n';

const countries = [
  'United States', 'China', 'Germany', 'India', 'Japan', 'United Kingdom',
  'South Korea', 'Italy', 'France', 'Canada', 'Mexico', 'Brazil',
  'Vietnam', 'Thailand', 'Indonesia', 'Turkey', 'Russia', 'Iran',
  'Cuba', 'North Korea', 'Syria', 'Taiwan', 'Australia', 'Netherlands'
];

function StatusBadge({ status }) {
  if (status === 'clear') return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"><CheckCircle className="h-3 w-3 mr-1" /> Clear</Badge>;
  if (status === 'flagged') return <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"><XCircle className="h-3 w-3 mr-1" /> Flagged</Badge>;
  return <Badge variant="outline"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
}

function RiskBadge({ level }) {
  const styles = {
    none: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    low: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    high: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    critical: 'bg-red-200 text-red-900 dark:bg-red-950 dark:text-red-200',
  };
  return <Badge className={styles[level] || styles.low}>{level}</Badge>;
}

export default function CompliancePage() {
  const { t } = useTranslation();
  const [hsInput, setHsInput] = useState('');
  const [tariffInput, setTariffInput] = useState({ hs_code: '', origin: '', destination: 'United States', value: '' });
  const [sanctionsInput, setSanctionsInput] = useState({ name: '', type: 'company', country: '' });
  const [exportInput, setExportInput] = useState({ description: '', hs_code: '', destination: '', end_use: '' });

  // HS Code Lookup
  const hsLookup = useMutation({
    mutationFn: (desc) => api.post('/compliance/hs-lookup', { product_description: desc }),
  });

  // Tariff Estimate
  const tariffEstimate = useMutation({
    mutationFn: (data) => api.post('/compliance/tariff-estimate', data),
  });

  // Sanctions Check
  const sanctionsCheck = useMutation({
    mutationFn: (data) => api.post('/compliance/sanctions-check', data),
  });

  // Export Control
  const exportCheck = useMutation({
    mutationFn: (data) => api.post('/compliance/export-control', data),
  });

  // Compliance Logs
  const { data: logs } = useQuery({
    queryKey: ['compliance-logs'],
    queryFn: () => api.get('/compliance/logs?limit=10').then(r => r.data),
  });

  // Sanctioned countries reference
  const { data: sanctionedRef } = useQuery({
    queryKey: ['sanctioned-countries'],
    queryFn: () => api.get('/compliance/sanctioned-countries').then(r => r.data),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t("compliance.complianceCenter")}</h1>
        <p className="text-muted-foreground">{t("compliance.hsCodesTariffsSanctions")}</p>
      </div>

      <Tabs defaultValue="hs-lookup">
        <TabsList className="flex-wrap">
          <TabsTrigger value="hs-lookup" className="gap-1"><Package className="h-4 w-4" /> HS Codes</TabsTrigger>
          <TabsTrigger value="tariff" className="gap-1"><Globe className="h-4 w-4" /> Tariffs</TabsTrigger>
          <TabsTrigger value="sanctions" className="gap-1"><ShieldAlert className="h-4 w-4" /> Sanctions</TabsTrigger>
          <TabsTrigger value="export" className="gap-1"><FileCheck className="h-4 w-4" /> Export Control</TabsTrigger>
          <TabsTrigger value="history" className="gap-1"><Clock className="h-4 w-4" /> History</TabsTrigger>
        </TabsList>

        {/* HS Code Lookup */}
        <TabsContent value="hs-lookup" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("compliance.hsCodeClassification")}</CardTitle>
              <CardDescription>AI-powered Harmonized System code lookup. Describe your product and get the most likely HS classifications.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t("compliance.productDescription")}</Label>
                <Textarea
                  value={hsInput}
                  onChange={e => setHsInput(e.target.value)}
                  placeholder={t("compliance.ph.productDesc") || "e.g., Stainless steel bolts, M10 x 50mm..."}
                  rows={3}
                />
              </div>
              <Button onClick={() => hsLookup.mutate(hsInput)} disabled={!hsInput || hsLookup.isLoading}>
                {hsLookup.isLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Classifying...</> : <><Search className="h-4 w-4 mr-2" /> Classify Product</>}
              </Button>

              {hsLookup.data?.data?.classifications && (
                <div className="space-y-3 pt-4">
                  {hsLookup.data.data.classifications.map((c, i) => (
                    <div key={i} className="p-3 rounded-lg border bg-card">
                      <div className="flex items-center justify-between mb-1">
                        <code className="text-lg font-bold text-primary">{c.hs_code}</code>
                        <Badge variant={c.confidence === 'high' ? 'default' : 'outline'}>{c.confidence}</Badge>
                      </div>
                      <p className="text-sm">{c.hs_description}</p>
                      <p className="text-xs text-muted-foreground mt-1">Chapter: {c.chapter}</p>
                      {c.notes && <p className="text-xs text-muted-foreground mt-1">{c.notes}</p>}
                    </div>
                  ))}
                  <Alert><AlertDescription className="text-xs">{hsLookup.data.data.disclaimer}</AlertDescription></Alert>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tariff Estimate */}
        <TabsContent value="tariff" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("compliance.tariffCalculator")}</CardTitle>
              <CardDescription>{t("compliance.estimateImportDutiesBased")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("compliance.hsCode")}</Label>
                  <Input value={tariffInput.hs_code} onChange={e => setTariffInput(p => ({...p, hs_code: e.target.value}))} placeholder="e.g., 731815" />
                </div>
                <div className="space-y-2">
                  <Label>{t("compliance.declaredValueUsd")}</Label>
                  <Input type="number" value={tariffInput.value} onChange={e => setTariffInput(p => ({...p, value: e.target.value}))} placeholder="e.g., 50000" />
                </div>
                <div className="space-y-2">
                  <Label>{t("compliance.originCountry")}</Label>
                  <Select value={tariffInput.origin} onValueChange={v => setTariffInput(p => ({...p, origin: v}))}>
                    <SelectTrigger><SelectValue placeholder={t("compliance.select") || "Select..."} /></SelectTrigger>
                    <SelectContent>{countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("compliance.destinationCountry")}</Label>
                  <Select value={tariffInput.destination} onValueChange={v => setTariffInput(p => ({...p, destination: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={() => tariffEstimate.mutate({
                hs_code: tariffInput.hs_code, origin_country: tariffInput.origin,
                destination_country: tariffInput.destination,
                declared_value: tariffInput.value ? parseFloat(tariffInput.value) : undefined
              })} disabled={!tariffInput.hs_code || !tariffInput.origin || tariffEstimate.isLoading}>
                {tariffEstimate.isLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Calculating...</> : 'Calculate Tariff'}
              </Button>

              {tariffEstimate.data?.data?.estimate && (
                <div className="p-4 rounded-lg border bg-card space-y-3 mt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-muted-foreground">{t("compliance.dutyRate")}</span> <span className="font-bold text-lg">{tariffEstimate.data.data.estimate.duty_rate_percent}%</span></div>
                    <div><span className="text-muted-foreground">{t("compliance.totalRate")}</span> <span className="font-bold text-lg">{tariffEstimate.data.data.estimate.total_estimated_rate}%</span></div>
                    {tariffEstimate.data.data.estimate.estimated_duty && (
                      <div><span className="text-muted-foreground">{t("compliance.estDuty")}</span> <span className="font-bold">${tariffEstimate.data.data.estimate.estimated_duty.toLocaleString()}</span></div>
                    )}
                    {tariffEstimate.data.data.estimate.total_estimated_cost && (
                      <div><span className="text-muted-foreground">{t("compliance.totalEstCost")}</span> <span className="font-bold">${tariffEstimate.data.data.estimate.total_estimated_cost.toLocaleString()}</span></div>
                    )}
                  </div>
                  {tariffEstimate.data.data.estimate.trade_agreements?.length > 0 && (
                    <div><span className="text-sm text-muted-foreground">{t("compliance.tradeAgreements")}</span> {tariffEstimate.data.data.estimate.trade_agreements.map((a, i) => <Badge key={i} variant="outline" className="ml-1">{a}</Badge>)}</div>
                  )}
                  <Alert><AlertDescription className="text-xs">{tariffEstimate.data.data.disclaimer}</AlertDescription></Alert>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sanctions Screening */}
        <TabsContent value="sanctions" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("compliance.sanctionsScreening")}</CardTitle>
              <CardDescription>{t("compliance.screenEntitiesAgainstOfac")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label>{t("compliance.entityName")}</Label>
                  <Input value={sanctionsInput.name} onChange={e => setSanctionsInput(p => ({...p, name: e.target.value}))} placeholder="Company or individual name" />
                </div>
                <div className="space-y-2">
                  <Label>{t("compliance.type")}</Label>
                  <Select value={sanctionsInput.type} onValueChange={v => setSanctionsInput(p => ({...p, type: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="company">{t("compliance.company")}</SelectItem>
                      <SelectItem value="individual">{t("compliance.individual")}</SelectItem>
                      <SelectItem value="vessel">{t("compliance.vessel")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("compliance.countryOptional")}</Label>
                <Select value={sanctionsInput.country} onValueChange={v => setSanctionsInput(p => ({...p, country: v}))}>
                  <SelectTrigger><SelectValue placeholder={t("compliance.selectCountry") || "Select country..."} /></SelectTrigger>
                  <SelectContent>{countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={() => sanctionsCheck.mutate({
                entity_name: sanctionsInput.name, entity_type: sanctionsInput.type, country: sanctionsInput.country || undefined
              })} disabled={!sanctionsInput.name || sanctionsCheck.isLoading}>
                {sanctionsCheck.isLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Screening...</> : <><Shield className="h-4 w-4 mr-2" /> Screen Entity</>}
              </Button>

              {sanctionsCheck.data?.data && (
                <div className="p-4 rounded-lg border bg-card space-y-3 mt-4">
                  <div className="flex items-center justify-between">
                    <StatusBadge status={sanctionsCheck.data.data.status} />
                    <RiskBadge level={sanctionsCheck.data.data.risk_level} />
                  </div>
                  {sanctionsCheck.data.data.checks?.map((check, i) => (
                    <div key={i} className="p-2 rounded bg-muted/50 text-sm">
                      <div className="flex items-center gap-2">
                        {check.match ? <XCircle className="h-4 w-4 text-red-500" /> : <CheckCircle className="h-4 w-4 text-green-500" />}
                        <span className="font-medium">{check.list}</span>
                      </div>
                      <p className="text-muted-foreground ml-6">{check.detail}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Reference: Sanctioned Countries */}
              {sanctionedRef && (
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium mb-2">{t("compliance.usSanctionedCountries")}</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.values(sanctionedRef.comprehensive || {}).map((c, i) => (
                      <Badge key={i} className="bg-red-100 text-red-800 text-xs">{c}</Badge>
                    ))}
                    {Object.values(sanctionedRef.partial || {}).map((c, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{c}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Export Control */}
        <TabsContent value="export" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("compliance.exportControlCheck")}</CardTitle>
              <CardDescription>{t("compliance.aipoweredEaritarClassificationGuidance")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t("compliance.producttechnologyDescription")}</Label>
                <Textarea value={exportInput.description} onChange={e => setExportInput(p => ({...p, description: e.target.value}))}
                  placeholder={t("compliance.ph.exportDesc") || "Describe the product or technology being exported..."} rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("compliance.destinationCountry")}</Label>
                  <Select value={exportInput.destination} onValueChange={v => setExportInput(p => ({...p, destination: v}))}>
                    <SelectTrigger><SelectValue placeholder={t("compliance.select") || "Select..."} /></SelectTrigger>
                    <SelectContent>{countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("compliance.hsCodeOptional")}</Label>
                  <Input value={exportInput.hs_code} onChange={e => setExportInput(p => ({...p, hs_code: e.target.value}))} placeholder="e.g., 847150" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("compliance.endUseOptional")}</Label>
                <Input value={exportInput.end_use} onChange={e => setExportInput(p => ({...p, end_use: e.target.value}))} placeholder="e.g., Manufacturing automotive components" />
              </div>
              <Button onClick={() => exportCheck.mutate({
                product_description: exportInput.description, hs_code: exportInput.hs_code || undefined,
                destination_country: exportInput.destination, end_use: exportInput.end_use || undefined
              })} disabled={!exportInput.description || !exportInput.destination || exportCheck.isLoading}>
                {exportCheck.isLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Analyzing...</> : <><FileCheck className="h-4 w-4 mr-2" /> Check Export Controls</>}
              </Button>

              {exportCheck.data?.data?.analysis && (
                <div className="p-4 rounded-lg border bg-card space-y-3 mt-4">
                  <div className="flex items-center gap-3">
                    <Badge className="text-sm">{exportCheck.data.data.analysis.classification}</Badge>
                    <RiskBadge level={exportCheck.data.data.analysis.risk_level} />
                    {exportCheck.data.data.analysis.requires_license && <Badge className="bg-red-100 text-red-800">{t("compliance.licenseRequired")}</Badge>}
                  </div>
                  {exportCheck.data.data.analysis.eccn && <p className="text-sm">ECCN: <code className="font-bold">{exportCheck.data.data.analysis.eccn}</code></p>}
                  {exportCheck.data.data.analysis.red_flags?.length > 0 && (
                    <div className="space-y-1">
                      {exportCheck.data.data.analysis.red_flags.map((f, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-red-600"><AlertTriangle className="h-3 w-3" /> {f}</div>
                      ))}
                    </div>
                  )}
                  {exportCheck.data.data.analysis.recommendations?.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{t("compliance.recommendations")}</p>
                      {exportCheck.data.data.analysis.recommendations.map((r, i) => (
                        <p key={i} className="text-sm text-muted-foreground ml-2">- {r}</p>
                      ))}
                    </div>
                  )}
                  <Alert><AlertDescription className="text-xs">{exportCheck.data.data.disclaimer}</AlertDescription></Alert>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("compliance.complianceHistory")}</CardTitle>
              <CardDescription>{t("compliance.recentComplianceChecksFor")}</CardDescription>
            </CardHeader>
            <CardContent>
              {logs?.logs?.length > 0 ? (
                <div className="space-y-2">
                  {logs.logs.map((log, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded border bg-card text-sm">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-xs">{log.type?.replace('_', ' ')}</Badge>
                        <span className="text-muted-foreground">{JSON.stringify(log.input || {}).slice(0, 60)}...</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{new Date(log.created_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">{t("compliance.noComplianceChecksYet")}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
